/* ============================================================================
   FALLAS DE ESCRITURA — REGISTRO GLOBAL

   `usePersistedState` devuelve `[valor, setValor, { cargado, error }]`, y de
   las **29 llamadas del legacy ninguna recoge el error**. Entre ellas:
   `bitacora`, `libroPiloto`, `vuelosDelDia`, `discrepancias`, `dutyTime`.

   ══════════════════════════════════════════════════════════════════════════
   LO QUE PASABA

   Si la escritura falla —cuota llena, modo privado, otra pestaña bloqueando la
   base— el vuelo **se sigue viendo en pantalla como guardado**. El piloto
   cierra la aplicación, vuelve, y no está.

   Un dato que se perdió en silencio es peor que uno que nunca se cargó: el
   piloto cree que lo tiene.

   ══════════════════════════════════════════════════════════════════════════
   POR QUÉ UN REGISTRO GLOBAL Y NO 29 CORRECCIONES

   Corregir las 29 llamadas funciona hoy y falla mañana: la número 30 se
   escribe sin el error y nadie lo nota, porque **no hay nada que se vea mal**.

   Acá las fallas se PUBLICAN desde el propio `usePersistedState`, y el
   cascarón las muestra. Un sitio nuevo queda cubierto sin que nadie tenga que
   acordarse de nada.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

/** Las fallas vivas, por clave. Una por clave: repetir la misma no agrega. */
const fallas = new Map();
const oyentes = new Set();

function avisar() {
  const lista = [...fallas.values()];
  for (const o of oyentes) {
    try { o(lista); } catch { /* un oyente roto no puede tumbar a los demás */ }
  }
}

/**
 * Registra que una escritura falló.
 *
 * @param {string} clave   qué se intentaba guardar
 * @param {Error|string} causa
 */
export function registrarFalla(clave, causa) {
  const k = String(clave || "(sin clave)");
  const previa = fallas.get(k);

  fallas.set(k, {
    clave: k,
    causa: causa?.message || String(causa || "causa desconocida"),
    /* Cuántas veces falló: una vez puede ser un tropiezo; veinte seguidas es
       que el almacenamiento no está funcionando. */
    veces: (previa?.veces || 0) + 1,
    desde: previa?.desde || new Date().toISOString(),
    ultima: new Date().toISOString(),
  });

  avisar();
}

/**
 * Marca que una clave volvió a guardarse bien.
 *
 * Sin esto, un tropiezo aislado dejaría la alarma encendida para siempre y el
 * piloto aprendería a ignorarla.
 */
export function registrarExito(clave) {
  const k = String(clave || "");
  if (fallas.delete(k)) avisar();
}

/** Las fallas vivas. */
export function fallasVivas() {
  return [...fallas.values()];
}

/** ¿Hay algo perdido ahora mismo? */
export function hayFallas() {
  return fallas.size > 0;
}

/**
 * Escucha los cambios. Devuelve la función para dejar de escuchar.
 */
export function escucharFallas(oyente) {
  if (typeof oyente !== "function") return () => {};
  oyentes.add(oyente);

  /* La primera llamada va protegida igual que las demás.

     Estaba suelta, y un oyente que reventara al suscribirse tumbaba a quien lo
     estuviera montando — que es el cascarón de la aplicación. La suite de
     fallos lo agarró en la primera corrida. */
  try { oyente([...fallas.values()]); } catch { /* ver `avisar` */ }

  return () => { oyentes.delete(oyente); };
}

/**
 * El texto que se le muestra al piloto.
 *
 * Dice **qué se perdió** y **qué hacer**, no «error al guardar»: un aviso sin
 * acción posible solo genera desconfianza.
 */
export function mensajeDeFallas(lista = fallasVivas()) {
  if (!Array.isArray(lista) || lista.length === 0) return null;

  /* Los nombres que el piloto reconoce. Una clave técnica —`libroPiloto`— no
     le dice qué perdió. */
  const NOMBRES = {
    bitacora: "la bitácora de vuelos",
    libroPiloto: "el libro de vuelo del piloto",
    vuelosDelDia: "los vuelos del día",
    discrepancias: "las discrepancias de mantenimiento",
    dutyTime: "el control de jornada",
    flota: "la flota",
    estadoSgsRegistros: "los registros del SGS",
  };

  /* Las claves llegan con el prefijo `estado:` que pone `usePersistedState`.
     Sin quitarlo, la tabla de nombres no encontraba ninguno y el piloto veía
     «estado:libroPiloto» en vez de «el libro de vuelo del piloto».

     Lo agarró la suite de fallos: el nombre técnico no dice qué se perdió. */
  const que = lista
    .map((f) => {
      const sinPrefijo = String(f.clave).replace(/^estado:/, "");
      return NOMBRES[sinPrefijo] || sinPrefijo;
    })
    .join(", ");

  return {
    titulo: "NO SE PUDO GUARDAR",
    detalle: `Se perdió lo último que cargaste en: ${que}. `
      + "Lo que ves en pantalla NO está guardado.",
    /* Las tres causas reales, en orden de frecuencia. */
    queHacer: "Puede ser que el almacenamiento esté lleno, que el navegador "
      + "esté en modo privado, o que la aplicación esté abierta en otra pestaña. "
      + "Cierra las otras pestañas y vuelve a intentar. Antes de cerrar, anota "
      + "los datos a mano.",
    claves: lista.map((f) => f.clave),
    veces: lista.reduce((s, f) => s + f.veces, 0),
  };
}

/** Para las pruebas. */
export function limpiarFallas() {
  fallas.clear();
  avisar();
}
